import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";

// Future Tavus Partner Advisor placement — deliberately disabled until
// the conversational advisor is integrated. No AI code/SDK exists yet.
const PARTNER_ADVISOR_ENABLED = false;
import {
  ArrowRight, CheckCircle2, QrCode, Package, Store, FlaskConical,
  LayoutDashboard, FileText, ShieldCheck, Handshake, TrendingUp,
  Droplets, ClipboardCheck, UserCheck, Wrench, Building2, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { Footer } from "@/components/Footer";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

const AUDIENCES = [
  "Plumbers", "Water filtration businesses", "Pump companies",
  "Tank cleaning businesses", "Rainwater specialists", "Bore contractors",
  "Irrigation businesses", "Rural suppliers", "Rural property specialists",
  "Property managers", "Real estate businesses", "Building inspectors",
  "Environmental consultants", "Hardware & trade retailers",
  "Water treatment businesses", "Other suitable professionals",
];

const BENEFITS = [
  { icon: FlaskConical, title: "Professional water testing", body: "Give customers access to AWARE laboratory testing and reporting without building your own lab service." },
  { icon: LayoutDashboard, title: "Partner Portal", body: "Secure access to the customer tests and reporting appropriate to your Partner account." },
  { icon: FileText, title: "Professional reports", body: "Your customers receive clear, professional AWARE water reports." },
  { icon: ShieldCheck, title: "Full Analysis / COA access", body: "Where supported, approved Partners can securely access Full Analysis / Certificate of Analysis information." },
  { icon: Sparkles, title: "Partner branding", body: "The Summary Report may carry your branding where configured. The analytical COA always remains AWARE-branded." },
  { icon: TrendingUp, title: "Referral commissions", body: "Eligible bookings attributed through your tracked referral channel may earn commission under your approved agreement." },
  { icon: Package, title: "Kits on hand", body: "Approved Partners can purchase AWARE sample kits upfront and keep them ready for customers." },
  { icon: Handshake, title: "Customer retention", body: "Water testing gives you another genuine reason to stay engaged with tank, bore and private-water customers." },
];

const STEPS = [
  { n: "1", title: "Apply", body: "Submit the Partner application — it takes a few minutes." },
  { n: "2", title: "AWARE reviews your business", body: "We assess suitability for the AWARE Partner Network." },
  { n: "3", title: "Partner access is approved", body: "Your Partner account is activated and the appropriate capabilities are assigned." },
  { n: "4", title: "Use your approved Partner services", body: "Depending on approval: refer customers, purchase kits, arrange testing, or operate as a Retail Stockist." },
  { n: "5", title: "Track testing and reporting", body: "Follow your customers' tests and reports in the secure Partner Portal." },
];

const FAQS = [
  {
    q: "Who can become an AWARE Partner?",
    a: "Australian trade and property businesses that already work with private-water properties — plumbers, filtration and pump specialists, tank and bore contractors, rural suppliers, property professionals, retailers and other suitable businesses. Every application is reviewed by AWARE.",
  },
  {
    q: "I'm a Pristine Water Systems (PWS) franchisee — do I apply here?",
    a: "No — PWS franchisees have their own dedicated pathway with regional territories, kit purchasing and the PWS partner portal already included. Visit the PWS Franchisee Network page instead. This page is for other businesses joining the general AWARE Partner Network.",
  },
  {
    q: "Is there a fee to join?",
    a: "Applying to the AWARE Partner Network is free. Commercial arrangements — such as kit purchasing and Retail Stockist terms — are provided to approved Partners.",
  },
  {
    q: "Can Referral Partners purchase kits?",
    a: "Yes. Approved Referral Partners may also be given access to purchase AWARE kits. Retail Stockist status is not required simply to purchase kits.",
  },
  {
    q: "How do referral commissions work?",
    a: "Eligible bookings must be attributed through your unique tracked Partner link or QR code (or another approved attribution method). Commission is calculated according to your approved commercial agreement — detailed terms are provided to approved Partners.",
  },
  {
    q: "Can I sell AWARE testing through my shop?",
    a: "Selected approved Partners may become AWARE Retail Stockists — holding kits in stock, arranging a customer's AWARE booking at the counter and selling the service through their business. Retail Stockist capability is separately approved and subject to AWARE commercial terms.",
  },
  {
    q: "Can I set my own retail price?",
    a: "Approved Retail Stockists operate under their commercial agreement, including their customer-facing retail arrangements. The details of AWARE's confidential Partner pricing are provided privately to approved Retail Stockists.",
  },
  {
    q: "Can my branding appear on reports?",
    a: "Where configured, the customer-facing Summary Report may carry your Partner branding. The analytical Certificate of Analysis (COA) always remains AWARE-branded — it is the laboratory document.",
  },
  {
    q: "Can I access customer results?",
    a: "Approved Partners access the tests and reports appropriate to their account through the secure Partner Portal, subject to existing Partner permissions and customer privacy protections.",
  },
  {
    q: "Do I need water-testing experience?",
    a: "No. AWARE performs the laboratory analysis and produces the reports. Partners simply connect their customers to testing — no laboratory or reporting infrastructure is required.",
  },
  {
    q: "How do I apply?",
    a: "Submit the Partner application — AWARE reviews every application before Partner access is activated.",
  },
];

const PORTAL_CARDS = [
  { icon: ClipboardCheck, label: "Tests in progress", value: "4", sub: "2 samples at the lab" },
  { icon: FileText, label: "Reports ready", value: "12", sub: "3 new this week" },
  { icon: UserCheck, label: "Customer tests", value: "38", sub: "across 26 properties" },
  { icon: Sparkles, label: "Partner branding", value: "On", sub: "Summary Reports branded" },
  { icon: ShieldCheck, label: "Full Analysis", value: "Enabled", sub: "secure COA access" },
  { icon: Package, label: "Kit access", value: "18 kits", sub: "on hand · reorder anytime" },
];

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`px-4 sm:px-6 lg:px-8 ${className}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const Kicker = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">{children}</div>
);

export default function PartnerProgram() {
  // FAQ from the Partner Program knowledge SSOT (safe fallback to FAQS)
  const [ssotFaq, setSsotFaq] = useState([]);
  useEffect(() => {
    api.get("/partner-program/faq")
      .then(({ data }) => { if (Array.isArray(data.faq) && data.faq.length) setSsotFaq(data.faq); })
      .catch(() => {});
  }, []);
  // Phase 2 — preserve the tracked invite token when a prospect reads
  // this page first and then clicks through to apply.
  const invite = new URLSearchParams(window.location.search).get("invite");
  const registerUrl = invite ? `/trade/register?invite=${encodeURIComponent(invite)}` : "/trade/register";
  useSEO({
    title: "AWARE Partner Program — Water Testing for Trade & Property Businesses",
    description:
      "Join the AWARE Partner Network. Refer customers, purchase AWARE test kits, arrange professional private-water testing and, for selected businesses, offer AWARE testing as a Retail Stockist.",
    url: `${SEO_DEFAULTS.baseUrl}/partners`,
    type: "website",
  });

  return (
    <div data-testid="partners-page" className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <Button asChild size="sm" className="btn-aqua rounded-full font-semibold">
            <Link to={registerUrl} data-testid="partners-header-cta">Become a Partner</Link>
          </Button>
        </div>
      </header>

      <main className="flex-1">
        {/* HERO */}
        <Section className="pt-16 pb-14 lg:pt-24 lg:pb-20 bg-slate-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-[0.07]"
            style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #22d3ee 0, transparent 40%), radial-gradient(circle at 85% 70%, #0ea5e9 0, transparent 35%)" }} />
          <div className="relative max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-400">AWARE Partner Network</div>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
              Partner with AWARE Water Testing
            </h1>
            <p className="mt-5 text-lg text-slate-200 leading-relaxed">
              Give your customers access to professional private-water testing — without building
              your own laboratory service.
            </p>
            <p className="mt-4 text-sm md:text-base text-slate-400 leading-relaxed">
              Approved AWARE Partners may be able to refer customers, purchase AWARE sample kits,
              arrange testing for their customers, access secure Partner reporting — and, for
              selected businesses, offer AWARE testing through a Retail Stockist arrangement.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-aqua rounded-full font-semibold px-7">
                <Link to={registerUrl} data-testid="partners-hero-cta">
                  Become an AWARE Partner <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full font-semibold px-7 bg-transparent border-slate-500 text-white hover:bg-slate-800 hover:text-white">
                <a href="#how-it-works" data-testid="partners-hero-secondary">See How It Works</a>
              </Button>
            </div>
          </div>
        </Section>

        {/* WHO IT IS FOR */}
        <Section id="who" className="py-16 lg:py-20">
          <Kicker>Who it's for</Kicker>
          <h2 className="mt-2 text-base md:text-lg font-bold text-slate-900 max-w-2xl font-display !text-2xl md:!text-3xl">
            Built for businesses already working with private-water properties
          </h2>
          <div className="mt-8 flex flex-wrap gap-2.5" data-testid="partners-audiences">
            {AUDIENCES.map((a) => (
              <span key={a} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 shadow-sm">
                <Droplets className="w-3.5 h-3.5 text-cyan-500" />{a}
              </span>
            ))}
          </div>
          <div data-testid="partners-pws-callout" className="mt-8 rounded-2xl bg-cyan-50 border border-cyan-200 p-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <div className="text-sm font-bold text-slate-900">Already a Pristine Water Systems (PWS) franchisee?</div>
              <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                PWS franchisees join through their own dedicated pathway — with regional territories,
                kit purchasing and the PWS partner portal already built in. This page is for other
                Australian trade and property businesses joining the general AWARE Partner Network.
              </p>
            </div>
            <Link
              to="/pws-franchisee-network"
              data-testid="partners-pws-link"
              className="inline-flex items-center gap-1.5 shrink-0 text-sm font-semibold text-cyan-700 hover:text-cyan-800 hover:underline"
            >
              PWS Franchisee Network <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Section>

        {/* PARTNER OPPORTUNITIES */}
        <Section id="opportunities" className="py-16 lg:py-20 bg-white border-y border-slate-200">
          <Kicker>Partner opportunities</Kicker>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900">
            Capabilities available to approved Partners
          </h2>
          <p className="mt-3 text-sm md:text-base text-slate-600 max-w-2xl">
            These aren't rigid partner types — they're opportunities. A Partner may hold any
            approved combination of capabilities.
          </p>

          <div className="mt-10 grid lg:grid-cols-3 gap-6">
            <div data-testid="partners-opportunity-refer" className="rounded-3xl border border-slate-200 bg-slate-50 p-7">
              <div className="w-11 h-11 rounded-2xl bg-cyan-100 flex items-center justify-center">
                <QrCode className="w-5 h-5 text-cyan-700" />
              </div>
              <h3 className="mt-4 font-display text-xl font-bold text-slate-900">Refer customers</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Approved Partners may receive a unique AWARE referral link or QR code. Eligible
                customer bookings made through your tracked referral channel can earn commission
                according to your approved commercial agreement.
              </p>
              <div className="mt-4 p-3.5 rounded-2xl bg-white border border-cyan-100">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-cyan-700">Earn from eligible referrals</div>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                  Approved AWARE Partners may earn commission when customers book through their
                  unique Partner link or QR code. Detailed commercial terms are provided to
                  approved Partners.
                </p>
              </div>
            </div>

            <div data-testid="partners-opportunity-kits" className="rounded-3xl border border-slate-200 bg-slate-50 p-7">
              <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-700" />
              </div>
              <h3 className="mt-4 font-display text-xl font-bold text-slate-900">Purchase AWARE kits</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-800">Keep AWARE kits ready for your customers.</span>{" "}
                Approved Partners can purchase AWARE test kits upfront, allowing them to keep
                sample kits on hand when customers need testing.
              </p>
              <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                This capability is available independently of Retail Stockist status — a Referral
                Partner may still purchase kits.
              </p>
            </div>

            <div data-testid="partners-opportunity-retail" className="rounded-3xl border border-slate-200 bg-slate-900 text-white p-7">
              <div className="w-11 h-11 rounded-2xl bg-cyan-500/20 flex items-center justify-center">
                <Store className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="mt-4 font-display text-xl font-bold">Retail Stockist opportunity</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                <span className="font-semibold text-white">Offer AWARE Water Testing through your business.</span>{" "}
                Selected approved Partners may become AWARE Retail Stockists.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs text-slate-300">
                {[
                  "Hold AWARE kits in stock",
                  "Arrange a customer's AWARE booking at the counter",
                  "Sell the service at your own retail price",
                  "Provide the customer with the collection kit",
                  "Customer returns the sample directly to AWARE",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-cyan-400 flex-shrink-0" />{t}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px] text-slate-400">
                Retail Stockist access is separately approved and subject to AWARE commercial terms.
              </p>
            </div>
          </div>
        </Section>

        {/* PARTNER BENEFITS */}
        <Section id="benefits" className="py-16 lg:py-20">
          <Kicker>Partner benefits</Kicker>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900">
            What AWARE Partners get
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="partners-benefits">
            {BENEFITS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
                <Icon className="w-5 h-5 text-cyan-600" />
                <h3 className="mt-3 font-semibold text-slate-900 text-sm">{title}</h3>
                <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl bg-slate-900 text-white p-6 flex items-start gap-4">
            <Wrench className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display font-bold">No laboratory infrastructure required</h3>
              <p className="mt-1 text-sm text-slate-300">
                AWARE handles the laboratory analysis, reporting platform and customer reports —
                you don't need to build any of it.
              </p>
            </div>
          </div>
        </Section>

        {/* HOW IT WORKS */}
        <Section id="how-it-works" className="py-16 lg:py-20 bg-white border-y border-slate-200">
          <Kicker>How it works</Kicker>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900">
            From application to approved Partner
          </h2>
          <ol className="mt-10 grid md:grid-cols-5 gap-5" data-testid="partners-how-it-works">
            {STEPS.map((s) => (
              <li key={s.n} className="relative rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="w-9 h-9 rounded-full bg-cyan-600 text-white font-display font-bold flex items-center justify-center">{s.n}</div>
                <h3 className="mt-3 font-semibold text-slate-900 text-sm">{s.title}</h3>
                <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </Section>

        {/* PORTAL PREVIEW */}
        <Section id="portal" className="py-16 lg:py-20">
          <Kicker>Partner Portal</Kicker>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900">
            Everything in one secure portal
          </h2>
          <p className="mt-3 text-sm md:text-base text-slate-600 max-w-2xl">
            A preview of what approved Partners see — sample data shown.
          </p>
          <div className="mt-8 rounded-3xl bg-slate-900 p-6 lg:p-8" data-testid="partners-portal-preview">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Partner Dashboard</span>
              </div>
              <span className="text-[10px] px-2.5 py-1 rounded-full bg-slate-800 text-slate-400 border border-slate-700">Sample data</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {PORTAL_CARDS.map(({ icon: Icon, label, value, sub }) => (
                <div key={label} className="rounded-2xl bg-slate-800/70 border border-slate-700 p-4">
                  <Icon className="w-4 h-4 text-cyan-400" />
                  <div className="mt-2 font-display text-2xl font-bold text-white">{value}</div>
                  <div className="text-[11px] font-semibold text-slate-300">{label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* COMMERCIAL STRUCTURE */}
        <Section id="commercial" className="py-16 lg:py-20 bg-white border-y border-slate-200">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <Kicker>Commercial structure</Kicker>
              <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900">
                A commercial model designed to work with your business
              </h2>
              <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">
                AWARE Partner arrangements can include referral commissions, wholesale kit
                purchasing, Partner pricing, Retail Stockist arrangements and account invoicing
                for approved businesses.
              </p>
              <p className="mt-3 text-xs text-slate-500">
                Commercial arrangements depend on the Partner's approved capabilities. Detailed
                terms are provided privately to approved Partners.
              </p>
            </div>
            <ul className="space-y-3" data-testid="partners-commercial-list">
              {[
                ["Referral commissions", "Earn on eligible tracked referrals under your agreement."],
                ["Wholesale kit purchasing", "Purchase AWARE kits upfront on Partner terms."],
                ["Partner pricing", "Commercial pricing appropriate to your Partner account."],
                ["Retail Stockist arrangements", "Sell AWARE testing through your business (separately approved)."],
                ["Account invoicing", "Approved businesses may access Partner invoice terms."],
              ].map(([t, b]) => (
                <li key={t} className="flex items-start gap-3 rounded-2xl bg-slate-50 border border-slate-200 p-4">
                  <Building2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{t}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{b}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* FAQ — served from the Partner Program knowledge SSOT
            (/api/partner-program/faq); hard-coded FAQS is the safe
            fallback if the endpoint is unavailable. */}
        <Section id="faq" className="py-16 lg:py-20">
          <Kicker>FAQ</Kicker>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900">
            Common questions
          </h2>
          <div className="mt-8 max-w-3xl space-y-3" data-testid="partners-faq">
            {(ssotFaq.length ? ssotFaq : FAQS).map(({ q, a }) => (
              <details key={q} className="group rounded-2xl bg-white border border-slate-200 px-5 py-4">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-3 font-semibold text-slate-900 text-sm">
                  {q}
                  <span className="text-cyan-600 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
          {PARTNER_ADVISOR_ENABLED && (
            <div className="mt-8 max-w-3xl rounded-2xl border border-cyan-200 bg-cyan-50 p-5" data-testid="partner-advisor-slot">
              <p className="text-sm font-semibold text-slate-900">Questions about becoming an AWARE Partner?</p>
              <p className="mt-1 text-sm text-slate-600">Our Partner Advisor can explain referrals, kits, Retail Stockist options, reports and how the program works.</p>
            </div>
          )}
        </Section>

        {/* FINAL CTA */}
        <Section className="py-16 lg:py-24 bg-slate-900 text-white">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-bold">
              Give your customers better information about their water.
            </h2>
            <p className="mt-3 text-slate-300">Join the AWARE Partner Network.</p>
            <Button asChild size="lg" className="mt-7 btn-aqua rounded-full font-semibold px-8">
              <Link to={registerUrl} data-testid="partners-final-cta">
                Apply to Become a Partner <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
